/**
 * Iter-373/374 (Admin UI) — Vendor Launch Promotion control panel
 *
 * Route:  /admin/vendor-promo
 * Access: admin / super_admin
 *
 * Lets operators toggle the marketplace launch promotion, extend its
 * end date, tune per-level discounts + badges, and edit the banner copy
 * — with no code deployment. Changes propagate to the marketing site
 * within ~60 seconds (ISR revalidate window on
 * fetchLaunchPromo → /api/vendor-promo/config).
 *
 * Signature Partner is a custom-quoted level and is deliberately not
 * editable here. The backend rejects any attempt to add "signature"
 * to `level_offers` with `signature_not_eligible` — this UI enforces
 * the same rule client-side by never rendering a Signature row.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, Save, RefreshCw, ExternalLink, AlertTriangle,
  Sparkles, Tag, Ban,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const AUTH = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const LEVEL_ROWS = [
  { key: 'preferred', name: 'Preferred Vendor', base: 79 },
  { key: 'premier',   name: 'Premier Vendor',   base: 149 },
  { key: 'exclusive', name: 'Exclusive Vendor', base: 299 },
];

/** ISO datetime -> value string for <input type="datetime-local"> (local TZ). */
function toLocalInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function fromLocalInput(v) {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export default function VendorPromoAdmin() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [server, setServer] = useState(null);       // last-known server state
  const [form, setForm]     = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/vendor-promo/config`, AUTH());
      setServer(data);
      setForm({
        active: !!data.active,
        ends_at_local: toLocalInput(data.ends_at),
        clear_ends_at: false,
        eyebrow:  data.eyebrow  || '',
        emoji:    data.emoji    || '🎉',
        headline: data.headline || '',
        detail:   data.detail   || '',
        months_free: data.months_free ?? 3,
        offers: {
          preferred: data.level_offers?.preferred || { discount_pct: 100, badge: '🎉 FREE FOR 3 MONTHS' },
          premier:   data.level_offers?.premier   || { discount_pct:  50, badge: '🏷️ 50% OFF FOR 3 MONTHS' },
          exclusive: data.level_offers?.exclusive || { discount_pct:  50, badge: '🏷️ 50% OFF FOR 3 MONTHS' },
        },
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || 'Could not load promo config');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const dirty = useMemo(() => {
    if (!server || !form) return false;
    return JSON.stringify({
      active: server.active,
      ends_at: server.ends_at,
      eyebrow: server.eyebrow, emoji: server.emoji, headline: server.headline, detail: server.detail,
      months_free: server.months_free, offers: server.level_offers,
    }) !== JSON.stringify({
      active: form.active,
      ends_at: form.clear_ends_at ? null : fromLocalInput(form.ends_at_local),
      eyebrow: form.eyebrow, emoji: form.emoji, headline: form.headline, detail: form.detail,
      months_free: Number(form.months_free), offers: form.offers,
    });
  }, [server, form]);

  const save = async () => {
    if (!form) return;
    // Local guard mirrors the server-side rule.
    if ('signature' in (form.offers || {})) {
      toast.error('Signature Partner cannot be part of the launch promotion.');
      return;
    }
    setSaving(true);
    try {
      const patch = {
        active: form.active,
        eyebrow: form.eyebrow.trim(),
        emoji: form.emoji.trim(),
        headline: form.headline.trim(),
        detail: form.detail.trim(),
        months_free: Number(form.months_free),
        level_offers: form.offers,
      };
      if (form.clear_ends_at) {
        patch.clear_ends_at = true;
      } else if (form.ends_at_local) {
        patch.ends_at = fromLocalInput(form.ends_at_local);
      }
      const { data } = await axios.patch(`${API}/admin/vendor-promo/config`, patch, AUTH());
      setServer(data);
      setForm((f) => ({ ...f, clear_ends_at: false }));
      toast.success('Launch promotion updated. Marketing site refreshes within ~60s.');
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'object' ? (d.message || d.code) : (d || 'Failed to save'));
    } finally { setSaving(false); }
  };

  const quickToggle = async (nextActive) => {
    setSaving(true);
    try {
      const { data } = await axios.patch(
        `${API}/admin/vendor-promo/config`,
        { active: nextActive },
        AUTH(),
      );
      setServer(data);
      setForm((f) => ({ ...f, active: data.active }));
      toast.success(nextActive ? 'Launch promotion enabled' : 'Launch promotion paused');
    } catch (e) {
      toast.error('Could not toggle the promotion');
    } finally { setSaving(false); }
  };

  if (loading || !form || !server) {
    return (
      <div className="p-8 text-ink-500 flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading launch promotion…
      </div>
    );
  }

  const isEffective = server.effective_now;

  return (
    <div className="max-w-4xl mx-auto p-6 md:p-8 space-y-6" data-testid="vendor-promo-admin">
      {/* Header + status */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.16em] font-bold text-cyan-700 mb-1">
            Vendor Marketplace · Admin
          </div>
          <h1 className="font-bold text-2xl md:text-3xl">Launch Promotion</h1>
          <p className="text-sm text-gray-500 mt-1">
            Controls the banner + per-level badges shown on the marketing site.
            Changes propagate within ~60 seconds — no code deploy.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Badge
            variant={isEffective ? 'default' : 'secondary'}
            className={isEffective ? 'bg-emerald-600 hover:bg-emerald-600 text-white' : 'bg-gray-200 text-gray-700'}
            data-testid="promo-status-badge"
          >
            {isEffective ? 'Live on marketing site' : 'Paused'}
          </Badge>
          <Button variant="outline" size="sm" onClick={load} data-testid="promo-refresh">
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {/* Quick on/off */}
      <Card className="p-5 flex items-center justify-between gap-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest font-bold text-gray-500 mb-1">
            Master switch
          </div>
          <div className="font-semibold text-lg">
            {form.active ? 'Launch promotion is enabled' : 'Launch promotion is paused'}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            When paused, the public banner disappears and no promo badges render on Vendor Level cards.
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-500">Off</span>
          <Switch
            checked={form.active}
            onCheckedChange={(v) => quickToggle(v)}
            disabled={saving}
            data-testid="promo-active-toggle"
          />
          <span className="text-xs text-gray-500">On</span>
        </div>
      </Card>

      {/* Copy panel */}
      <Card className="p-5 md:p-6 space-y-4">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-cyan-600" />
          <div className="font-semibold">Banner copy</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-1">
            <Label className="text-xs uppercase tracking-wider text-gray-500">Emoji</Label>
            <Input value={form.emoji} onChange={(e) => setForm({ ...form, emoji: e.target.value })} maxLength={8} data-testid="promo-emoji-input" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-wider text-gray-500">Eyebrow</Label>
            <Input value={form.eyebrow} onChange={(e) => setForm({ ...form, eyebrow: e.target.value })} maxLength={64} data-testid="promo-eyebrow-input" />
          </div>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-gray-500">Headline</Label>
          <Input value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} maxLength={280} data-testid="promo-headline-input" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-gray-500">Detail</Label>
          <Textarea rows={4} value={form.detail} onChange={(e) => setForm({ ...form, detail: e.target.value })} maxLength={1000} data-testid="promo-detail-input" />
          <p className="text-[10px] text-gray-400 mt-1">
            Tip: mention payment processing fees + marketplace platform fees. The signup wizard shows this verbatim.
          </p>
        </div>
      </Card>

      {/* Schedule */}
      <Card className="p-5 md:p-6 space-y-4">
        <div className="flex items-center gap-2">
          <Tag className="w-4 h-4 text-cyan-600" />
          <div className="font-semibold">Schedule</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-500">Ends at (local time)</Label>
            <Input
              type="datetime-local"
              value={form.clear_ends_at ? '' : form.ends_at_local}
              onChange={(e) => setForm({ ...form, ends_at_local: e.target.value, clear_ends_at: false })}
              disabled={form.clear_ends_at}
              data-testid="promo-ends-at-input"
            />
            <p className="text-[10px] text-gray-400 mt-1">
              After this moment the marketing site will hide the banner + badges automatically.
              Leave empty for &ldquo;no end date&rdquo;.
            </p>
          </div>
          <div className="flex items-end">
            <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer" data-testid="promo-clear-ends-at">
              <input
                type="checkbox"
                checked={form.clear_ends_at}
                onChange={(e) => setForm({ ...form, clear_ends_at: e.target.checked })}
              />
              Clear end date (open-ended promotion)
            </label>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-500">Months free</Label>
            <Input
              type="number"
              min={0}
              max={36}
              value={form.months_free}
              onChange={(e) => setForm({ ...form, months_free: e.target.value })}
              data-testid="promo-months-input"
            />
            <p className="text-[10px] text-gray-400 mt-1">Shown in the effective-price copy on tier cards.</p>
          </div>
        </div>
      </Card>

      {/* Per-level offers */}
      <Card className="p-5 md:p-6 space-y-4">
        <div className="flex items-center gap-2">
          <Tag className="w-4 h-4 text-cyan-600" />
          <div className="font-semibold">Per-Vendor-Level offers</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {LEVEL_ROWS.map((row) => {
            const offer = form.offers[row.key] || { discount_pct: 0, badge: '' };
            const pct = Number(offer.discount_pct || 0);
            const effective = Math.round(row.base * (1 - pct / 100));
            return (
              <div key={row.key} className="border border-gray-200 rounded-md p-4 space-y-3" data-testid={`offer-${row.key}`}>
                <div className="flex items-baseline justify-between">
                  <div className="font-mono uppercase text-[10px] tracking-widest font-bold text-cyan-700">
                    {row.name}
                  </div>
                  <div className="text-xs text-gray-500">Base ${row.base}/mo</div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-gray-500">Discount %</Label>
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    value={pct}
                    onChange={(e) => setForm({
                      ...form,
                      offers: { ...form.offers, [row.key]: { ...offer, discount_pct: Math.max(0, Math.min(100, Number(e.target.value || 0))) } },
                    })}
                    data-testid={`offer-${row.key}-pct`}
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-gray-500">Badge</Label>
                  <Input
                    value={offer.badge}
                    onChange={(e) => setForm({
                      ...form,
                      offers: { ...form.offers, [row.key]: { ...offer, badge: e.target.value } },
                    })}
                    maxLength={64}
                    data-testid={`offer-${row.key}-badge`}
                  />
                </div>
                <div className="text-xs text-gray-600 bg-gray-50 border border-gray-100 rounded-sm px-2 py-1">
                  {pct === 0
                    ? 'No discount — this level shows base price only.'
                    : pct === 100
                      ? `First ${form.months_free || 3} months FREE, then $${row.base}/mo.`
                      : `First ${form.months_free || 3} months: $${effective}/mo, then $${row.base}/mo.`}
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex items-start gap-2 text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-sm p-3">
          <Ban className="w-4 h-4 text-rose-600 mt-0.5 flex-shrink-0" />
          <div>
            <strong className="text-gray-800">Signature Partner is not editable here.</strong> Signature Partner is custom-quoted per contract and is deliberately excluded from the launch promotion by product policy. The API will reject any attempt to add it.
          </div>
        </div>
      </Card>

      {/* Save bar */}
      <div className="sticky bottom-4 z-10 bg-white border border-gray-200 rounded-md shadow-lg p-4 flex flex-col md:flex-row md:items-center gap-3 md:justify-between">
        <div className="text-xs text-gray-500 flex items-center gap-2">
          {dirty ? (
            <>
              <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
              Unsaved changes — the marketing site is still showing the last-saved copy.
            </>
          ) : (
            <>Last saved {server.updated_at ? new Date(server.updated_at).toLocaleString() : '—'}</>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={load} disabled={saving} data-testid="promo-revert">
            Revert
          </Button>
          <Button
            onClick={save}
            disabled={saving || !dirty}
            className="bg-cyan-600 hover:bg-cyan-700 text-white"
            data-testid="promo-save"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
            Save changes
          </Button>
          <a
            href="https://vendors.ievntur.com/pricing"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900 underline underline-offset-4"
            data-testid="promo-preview-link"
          >
            Preview <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </div>
    </div>
  );
}
